import React, { useEffect } from "react";
import { TestTube, Users, ChartLine, Repeat, Lightbulb } from "lucide-react";

import { ProjectHero } from "../ui/ProjectHero/ProjectHero";
import { VideoEmbed } from "../ui/VideoEmbed/VideoEmbed";
import { ProjectOverview } from "../ui/ProjectOverview/ProjectOverview";
import { BulletItem, BulletList } from "../ui/Bullets/Bullets";
import { KeyFeatures } from "../ui/KeyFeatures/KeyFeatures";
import { ProcessSection } from "../ui/ProcessSection/ProcessSection";
import { ProjectFooter } from "../ui/ProjectFooter/ProjectFooter";
import Footer from "../../../container/InlineFooter/InlineFooter";

import images from "../../../constants/images";

import "./EmergingIndustries.scss";

import emerginIndustries from "../../../assets/videos/emerging-industries-final.mp4";

const EmergingIndustries = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const bulletsListLeft = (
    <BulletList>
      <BulletItem>
        Rapidly identifying viable business opportunities in nascent markets
      </BulletItem>
      <BulletItem>Validating product concepts under uncertainty</BulletItem>
      <BulletItem>Aligning strategy with emerging customer needs</BulletItem>
      <BulletItem>Demonstrating value quickly to stakeholders</BulletItem>
    </BulletList>
  );

  const bulletsListRight = (
    <BulletList>
      <BulletItem>
        Mapped emerging industry landscapes and identified potential
        opportunities
      </BulletItem>
      <BulletItem>Created MVP concepts to test hypotheses quickly</BulletItem>
      <BulletItem>
        Used structured feedback and market data to refine offerings
      </BulletItem>
      <BulletItem>
        Delivered actionable recommendations to stakeholders, empowering them to
        move confidently in uncertain markets
      </BulletItem>
    </BulletList>
  );

  return (
    <div className="main-project-container">
      <ProjectHero
        title="Exploring Emerging Industries"
        subtitle="Understanding uncertainty and building validated solutions for nascent markets"
        category="CONCEPT"
        client="INFO Business Innovation Partner"
        role="Business Innovation Consultant"
        year="2019 - 2020"
        platform="Innovation Consulting & Emerging Industry Exploration"
        technologies={[
          "Market research frameworks",
          " Business model validation tools",
          " Prototyping & conceptual design (e.g., Figma, Miro)",
          " Strategic planning and workshops",
          " Client engagement tools (e.g., Notion, Google Workspace)",
        ]}
      />
      <ProjectOverview
        challengeContent={[
          "Navigating ambiguity and building confidence in untested markets",
          "Clients often had little precedent, limited data, and high risk tolerance. The main challenges were:",
        ]}
        extraChallengeContent={[
          "This wasn’t just consulting — it was helping clients learn how to learn in new industries.",
        ]}
        bulletsLeft={bulletsListLeft}
        bulletsRight={bulletsListRight}
        solutionContent={[
          "Structured exploration and rapid validation with measurable insights",
          "I worked as a Business Innovation Consultant on multiple client engagements, facilitating workshops, market research, and early validation exercises. Together, we:",
        ]}
        metrics={[
          {
            value: "5+ clients",
            label: "Engaged in early-stage emerging industry projects",
          },
          {
            value: "Rapid validation",
            label: "Concepts tested within weeks of ideation",
          },
          // {
          //   value: "High-confidence recommendations",
          //   label: "Delivered actionable insights to stakeholders",
          // },
          {
            value: "Cross-industry",
            label: "Learned and applied principles across multiple sectors",
          },
          {
            value: "Workshops & sessions",
            label: "10+ structured sessions guiding strategic decisions",
          },
        ]}
        // metrics={[]}
      />
      <VideoEmbed
        src={emerginIndustries}
        title="Exploring Emerging Industries – INFO Insights"
        controls={true}
        description="A short video summarizing our approach to helping clients navigate uncertainty, validate early concepts, and make confident strategic decisions in emerging markets."
        poster={images.infoLogo}
      />
      <KeyFeatures
        title="Guided Exploration in Uncertainty"
        description="Tools, frameworks, and approaches designed to support emerging industry clients."
        features={[
          {
            icon: Lightbulb,
            title: "Opportunity Mapping",
            description:
              "Identify potential growth areas and untapped market niches.",
          },
          {
            icon: TestTube,
            title: "Rapid Hypothesis Validation",
            description:
              "Test ideas quickly to reduce risk and inform strategic decisions.",
          },
          {
            icon: Users,
            title: "Collaborative Workshops",
            description:
              "Facilitate structured sessions to align teams and stakeholders.",
          },
          {
            icon: ChartLine,
            title: "Data-informed Decision Making",
            description:
              "Leverage early market signals and insights to guide strategy.",
          },
          {
            icon: Repeat,
            title: "Iterative Learning",
            description:
              "Refine products, services, and strategies through repeated testing.",
          },
        ]}
      />
      <ProcessSection
        title="Exploring Emerging Industries with Confidence"
        description="A structured, iterative approach to help clients navigate uncertain markets."
        phases={[
          {
            number: "01",
            title: "Industry Discovery",
            description:
              "Research emerging sectors, analyze trends, and identify potential opportunities.",
            deliverables: [
              "Market landscape reports",
              "Opportunity heatmaps",
              "Competitive benchmarking",
            ],
          },
          {
            number: "02",
            title: "Hypothesis Formation",
            description:
              "Define testable business or product hypotheses to guide early experiments.",
            deliverables: [
              "Hypothesis statements",
              "Success metrics",
              "Initial MVP concepts",
            ],
          },
          {
            number: "03",
            title: "Rapid Validation",
            description:
              "Run experiments with early adopters and stakeholders to validate assumptions.",
            deliverables: [
              "User feedback sessions",
              "Prototype testing",
              "Data-driven insights",
            ],
          },
          {
            number: "04",
            title: "Recommendations & Roadmap",
            description:
              "Deliver actionable insights and a strategic roadmap for next steps in the emerging market.",
            deliverables: [
              "Validated recommendations",
              "Client roadmap",
              "Iterative improvement plan",
            ],
          },
        ]}
      />
      <ProjectFooter
        impactTitle="Helping clients move forward with confidence in emerging markets"
        impactLabel="Project Impact"
        impactDescription="By structuring exploration, hypothesis testing, and early validation, the project enabled clients to make informed strategic decisions despite high uncertainty. Teams were empowered to experiment, learn quickly, and iterate confidently—building a foundation for future growth in emerging industries."
        nextProject={{
          title: "Han Open Science Platform",
          link: "/project/han",
        }}
        conclusion={{
          label: "Design / Engineering Reflection",
          title: "Gaining clarity in uncertain markets",
          description:
            "Through research, ideation, and rapid validation, we were able to provide actionable insights to clients operating in emerging industries. The project helped identify key opportunities, surface risks early, and align stakeholders around strategic decisions. Even without a final product, the work laid the groundwork for informed decision-making, faster experimentation, and more confident investment in new business ideas. The emphasis on methodical exploration and structured problem-solving ensured that the agency could continue supporting clients navigating highly uncertain markets.",
        }}
      />
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Footer />
      </div>
    </div>
  );
};

export { EmergingIndustries };

/*
Project Context

INFO Business Innovation Partner Agency works with clients operating in emerging industries—companies forming around new products, services, or ideas in the early stages of adoption.

Emerging industries are inherently uncertain. Success requires quickly understanding market dynamics, validating products and services, and iterating rapidly. Our team was tasked with helping clients navigate these uncertainties while identifying new opportunities for growth and innovation.


My Role

Business Innovation Consultant

Responsibilities included:

Leading market research and emerging industry analysis

Facilitating workshops and ideation sessions

Helping clients define testable hypotheses and validation plans

Coaching teams on agile, iterative decision-making in high-uncertainty environments

*/
